import { useState } from "react";
import { Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { useBillingEvents, useBillingSummary } from "@/hooks/useBilling";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { PricingRuleEditor } from "@/components/billing/PricingRuleEditor";

const EVENT_TYPE_LABELS: Record<string, string> = {
  vm_create: "VM Created",
  vm_resize: "VM Resized",
  vm_delete: "VM Deleted",
  tier_change: "Tier Changed",
  backup_change: "Backup Changed",
};

const WEBHOOK_STATUS_COLORS: Record<string, string> = {
  sent: "text-green-400",
  pending: "text-yellow-400",
  failed: "text-red-400",
  skipped: "text-gray-500",
};

export default function Billing() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data: summary, isLoading: summaryLoading, isError: summaryError, error: summaryErr } = useBillingSummary(year, month);
  const { data: events, isLoading: eventsLoading } = useBillingEvents(page * limit, limit);

  const prevMonth = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };

  const fmt = (n: number) =>
    n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const monthName = new Date(year, month - 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Billing & Usage</h1>
        <p className="mt-1 text-sm text-gray-400">
          View taxation tickets and monthly cost summary
        </p>
      </div>

      {/* Pricing Configuration */}
      <PricingRuleEditor />

      {/* Month selector + summary */}
      <div className="flex items-center gap-4">
        <button
          onClick={prevMonth}
          className="rounded-lg border border-gray-700 p-2 hover:bg-gray-800"
        >
          <ChevronLeft className="h-4 w-4 text-gray-400" />
        </button>
        <span className="text-base font-medium text-gray-200 min-w-[160px] text-center">
          {monthName}
        </span>
        <button
          onClick={nextMonth}
          className="rounded-lg border border-gray-700 p-2 hover:bg-gray-800"
        >
          <ChevronRight className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      {/* Summary cards */}
      {summaryError ? (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <p className="text-sm text-red-400">
            Failed to load billing summary: {(summaryErr as Error)?.message ?? "Unknown error"}
          </p>
        </div>
      ) : summaryLoading ? (
        <LoadingSpinner />
      ) : summary ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <p className="text-sm text-gray-400">Monthly Total</p>
            <p className="mt-1 text-2xl font-bold text-blue-400">
              {fmt(summary.total_monthly_cost)} EUR
            </p>
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <p className="text-sm text-gray-400">Active VMs</p>
            <p className="mt-1 text-2xl font-bold text-gray-100">
              {summary.vm_count}
            </p>
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-5">
            <p className="text-sm text-gray-400">Billing Events</p>
            <p className="mt-1 text-2xl font-bold text-gray-100">
              {summary.events_count}
            </p>
          </div>
        </div>
      ) : null}

      {/* Events table */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-2 border-b border-gray-800 px-6 py-4">
          <Receipt className="h-5 w-5 text-blue-400" />
          <h2 className="text-base font-semibold text-gray-200">
            Taxation Tickets
          </h2>
        </div>

        {eventsLoading ? (
          <div className="flex justify-center p-8">
            <LoadingSpinner />
          </div>
        ) : events && events.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-left text-gray-400">
                    <th className="px-6 py-3 font-medium">Date</th>
                    <th className="px-6 py-3 font-medium">Type</th>
                    <th className="px-6 py-3 font-medium">Description</th>
                    <th className="px-6 py-3 font-medium text-right">Delta</th>
                    <th className="px-6 py-3 font-medium text-right">New Cost</th>
                    <th className="px-6 py-3 font-medium text-center">Webhook</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr
                      key={event.id}
                      className="border-b border-gray-800/50 hover:bg-gray-800/30"
                    >
                      <td className="px-6 py-3 text-gray-300">
                        {new Date(event.created_at).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-3">
                        <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-300">
                          {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-400 max-w-xs truncate">
                        {event.description ?? "-"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <span
                          className={
                            event.delta_amount >= 0
                              ? "text-amber-400"
                              : "text-green-400"
                          }
                        >
                          {event.delta_amount >= 0 ? "+" : ""}
                          {fmt(event.delta_amount)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right text-gray-300">
                        {event.new_monthly_cost != null
                          ? `${fmt(event.new_monthly_cost)} EUR`
                          : "-"}
                      </td>
                      <td className="px-6 py-3 text-center">
                        <span
                          className={`text-xs font-medium ${
                            WEBHOOK_STATUS_COLORS[event.webhook_status] ?? "text-gray-500"
                          }`}
                        >
                          {event.webhook_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-800 px-6 py-3">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="text-sm text-gray-400 hover:text-gray-200 disabled:opacity-30"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {page + 1}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={events.length < limit}
                className="text-sm text-gray-400 hover:text-gray-200 disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No billing events for this period.
          </div>
        )}
      </div>
    </div>
  );
}
